import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useAuth } from "@/hooks/useAuth";
import LanguageToggle from "@/components/LanguageToggle";
import ThemedLogo from "@/components/ThemedLogo";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { localizedPath, lang } = useLocalizedPath();
  const { hasAdminAccess } = useAuth();
  
  const isHomePage = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const navItems = [
    { label: t("nav.about"), href: localizedPath("/about") },
    { label: t("nav.talent"), href: localizedPath("/talent") },
    { label: t("nav.services"), href: localizedPath("/services") },
    { label: t("nav.join"), href: localizedPath("/for-talent") },
    { label: t("nav.contact"), href: localizedPath("/contact") },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // On inner pages, always show scrolled style
  const showDarkHeader = isScrolled || !isHomePage;

  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut" as const,
        when: "afterChildren" as const,
      },
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeInOut" as const,
        when: "beforeChildren" as const,
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    closed: { 
      opacity: 0, 
      x: -16,
      transition: { duration: 0.2 }
    },
    open: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3, ease: "easeOut" as const }
    },
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showDarkHeader 
          ? "bg-background/95 backdrop-blur-sm border-b border-border" 
          : "bg-transparent"
      }`}
    >
      <div className="container-wide px-5 sm:px-6 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">
          {/* Logo & Admin Icon */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to={localizedPath("/")} className="flex items-center mt-2">
              <ThemedLogo 
                size="md"
                variant={showDarkHeader ? "themed" : "light"}
              />
            </Link>
            {hasAdminAccess && (
              <Link
                to="/admin"
                className={`p-2 rounded-md transition-colors flex items-center justify-center mt-2 ${
                  showDarkHeader 
                    ? "text-muted-foreground hover:text-foreground hover:bg-muted" 
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
                aria-label="Admin Dashboard"
              >
                <Settings size={20} />
              </Link>
            )}
          </div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center gap-5 absolute left-1/2 -translate-x-1/2 mt-5" aria-label="Main navigation">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`nav-link ${
                  showDarkHeader 
                    ? "text-muted-foreground hover:text-foreground" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right side - Language toggle & CTA */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <LanguageToggle 
              variant="header" 
              className={showDarkHeader ? "" : "[&_button]:text-white/80 [&_button:hover]:text-white [&_button[aria-pressed=true]]:text-white [&_button[aria-pressed=true]]:border-white [&_span]:text-white/30"}
            />
            <Button asChild variant="navCta" size="hero" className={showDarkHeader ? "" : "bg-white text-primary hover:bg-white/90"}>
              <Link to={localizedPath("/contact")}>{t("nav.getStarted")}</Link>
            </Button>
          </div>

          {/* Mobile/Tablet Menu Button */}
          <motion.button
            className={`lg:hidden p-3 -mr-2 rounded-lg min-h-[48px] min-w-[48px] flex items-center justify-center ${
              showDarkHeader ? "text-primary active:bg-muted" : "text-white active:bg-white/10"
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X size={24} aria-hidden="true" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu size={24} aria-hidden="true" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile/Tablet Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              id="mobile-menu" 
              className="lg:hidden bg-background border-t border-border overflow-hidden"
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
            >
              <nav className="flex flex-col py-4" aria-label="Mobile navigation">
                {navItems.map((item, index) => (
                  <motion.div key={item.label} variants={itemVariants}>
                    <Link
                      to={item.href}
                      className="flex items-center text-lg font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 px-4 py-4 min-h-[56px] transition-colors active:bg-muted"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
                {hasAdminAccess && (
                  <motion.div variants={itemVariants}>
                    <Link
                      to="/admin"
                      className="flex items-center gap-3 text-lg font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 px-4 py-4 min-h-[56px] transition-colors active:bg-muted"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Settings size={20} />
                      Admin
                    </Link>
                  </motion.div>
                )}
                <motion.div variants={itemVariants} className="px-4 py-4 border-t border-border mt-2">
                  <LanguageToggle variant="footer" />
                </motion.div>
                <motion.div variants={itemVariants} className="px-4 pt-2 pb-4">
                  <Button asChild variant="navCta" size="hero" className="w-full min-h-[52px]">
                    <Link to={localizedPath("/contact")} onClick={() => setIsMobileMenuOpen(false)}>
                      {t("nav.getStarted")}
                    </Link>
                  </Button>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
